import posthog, { type LogAttributes } from 'posthog-js'

import { isIgnoredErrorMessage } from '@/lib/ignoredErrors'
import type { AppPageProps } from '@/types'

let isInitialized = false
let replaysOnErrorSampleRate = 1.0

/**
 * Reset module-level initialization state for unit testing.
 */
export function _resetPostHogStateForTesting(): void {
  isInitialized = false
  replaysOnErrorSampleRate = 1.0
}

/**
 * Trigger session replay recording when an error is detected.
 *
 * If session recording is already running, this is a no-op. Otherwise, checks
 * posthog_replays_on_error_sample_rate and starts session recording with sampling
 * override so the buffered user interactions preceding the error are preserved and sent.
 */
export function triggerSessionReplayOnError(): void {
  if (typeof window === 'undefined' || !isInitialized) {
    return
  }

  if (
    typeof posthog.sessionRecordingStarted === 'function' &&
    posthog.sessionRecordingStarted()
  ) {
    return
  }

  if (replaysOnErrorSampleRate <= 0.0) {
    return
  }

  if (
    replaysOnErrorSampleRate < 1.0 &&
    Math.random() > replaysOnErrorSampleRate
  ) {
    return
  }

  try {
    if (typeof posthog.startSessionRecording === 'function') {
      posthog.startSessionRecording(true)
    }
  } catch (error) {
    console.warn('[PostHog] Failed to start session recording on error:', error)
  }
}

/**
 * Initialize the PostHog client SDK for the web frontend.
 */
export function initPostHog(initialProps: AppPageProps): void {
  if (typeof window === 'undefined' || isInitialized) {
    return
  }

  // Never initialize PostHog in demo/testing modes
  if (initialProps.isDemo) {
    return
  }

  // Never initialize PostHog on local or testing hosts (*.test, localhost, 127.0.0.1)
  if (
    typeof window !== 'undefined' &&
    window.location?.hostname &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.endsWith('.test'))
  ) {
    return
  }

  const settings = initialProps.settings
  const apiKey = settings?.posthog_api_key

  if (!apiKey || apiKey.trim() === '') {
    return
  }

  const rawHost = settings?.posthog_host || ''
  const isEu =
    rawHost.includes('eu.i.posthog.com') || rawHost.includes('eu.posthog.com')
  const defaultUiHost = isEu
    ? 'https://eu.posthog.com'
    : 'https://us.posthog.com'
  const uiHost = settings?.posthog_ui_host || defaultUiHost

  if (typeof settings.posthog_replays_on_error_sample_rate === 'number') {
    replaysOnErrorSampleRate = settings.posthog_replays_on_error_sample_rate
  }

  try {
    posthog.init(apiKey.trim(), {
      // Tunnel through first-party Laravel proxy to prevent ad-blocker dropouts
      api_host: '/ingest',
      ui_host: uiHost,
      autocapture: true,
      capture_performance: true,
      capture_pageview: 'history_change',
      capture_pageleave: true,
      logs: {
        serviceName: 'filebuddy-web',
        captureConsoleLogs:
          typeof settings.posthog_capture_console_logs === 'boolean'
            ? settings.posthog_capture_console_logs
            : true,
      },
      enable_recording_console_log: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*',
        sampleRate:
          typeof settings.posthog_replays_session_sample_rate === 'number'
            ? settings.posthog_replays_session_sample_rate
            : 0.0,
      },
      capture_exceptions: true,
      persistence: 'localStorage+cookie',
      before_send: (event) => {
        if (event && event.event === '$exception') {
          const props = event.properties as
            | {
                $exception_message?: string
                $exception_type?: string
                $exception_list?: Array<{ type?: string; value?: string }>
              }
            | undefined

          const isIgnored =
            isIgnoredErrorMessage(props?.$exception_message) ||
            isIgnoredErrorMessage(props?.$exception_type) ||
            (Array.isArray(props?.$exception_list) &&
              props!.$exception_list.some(
                (item) =>
                  isIgnoredErrorMessage(item?.value) ||
                  isIgnoredErrorMessage(item?.type),
              ))

          if (isIgnored) {
            return null
          }

          triggerSessionReplayOnError()
        }
        return event
      },
      loaded: (ph) => {
        isInitialized = true
        ph.register({
          client: 'web',
          platform: 'web',
          environment: 'production',
          app_version: initialProps.appVersion || '2.0.0',
        })
        const authUser = initialProps.auth?.user
        if (authUser && typeof authUser.id === 'number') {
          ph.identify(String(authUser.id), {
            email: authUser.email,
            name: authUser.name,
            plan: initialProps.auth?.plan?.slug,
            roles: authUser.roles ?? [],
            client: 'web',
            platform: 'web',
          })
        }
      },
    })
  } catch (error) {
    console.warn('[PostHog] Initialization error:', error)
  }
}

/**
 * Returns whether the PostHog SDK has been successfully initialized.
 */
export function isPostHogInitialized(): boolean {
  return isInitialized
}

/**
 * Sync authenticated user identity with PostHog on auth state changes.
 */
export function syncPostHogUser(props?: Partial<AppPageProps>): void {
  if (typeof window === 'undefined') {
    return
  }

  const user = props?.auth?.user

  if (user && typeof user.id === 'number') {
    posthog.identify(String(user.id), {
      email: user.email,
      name: user.name,
      plan: props?.auth?.plan?.slug,
      roles: user.roles ?? [],
      client: 'web',
      platform: 'web',
    })
  } else {
    // Only reset if an identified session was previously active
    const distinctId = posthog.get_distinct_id()
    if (
      distinctId &&
      !distinctId.startsWith('$device:') &&
      !distinctId.startsWith('anon_')
    ) {
      posthog.reset()
    }
  }
}

/**
 * Track an Inertia pageview event in PostHog.
 */
export function trackPostHogPageview(
  url: string,
  component: string,
  extraProperties?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') {
    return
  }

  posthog.capture('$pageview', {
    $current_url: url,
    component,
    ...extraProperties,
  })
}

/**
 * Capture an exception in PostHog Error Tracking.
 */
export function capturePostHogException(
  error: unknown,
  extraProperties?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') {
    return
  }

  const rawMessage =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : typeof error === 'object' && error !== null && 'message' in error
          ? String((error as { message: unknown }).message)
          : null

  if (isIgnoredErrorMessage(rawMessage)) {
    return
  }

  triggerSessionReplayOnError()

  const normalizedError =
    error instanceof Error
      ? error
      : typeof error === 'string'
        ? new Error(error)
        : new Error(JSON.stringify(error))

  posthog.captureException(normalizedError, extraProperties)
}

/**
 * Structured logger helper for PostHog Logs.
 */
export const posthogLogger = {
  trace: (message: string, properties?: LogAttributes): void => {
    if (typeof window !== 'undefined') {
      posthog.logger.trace(message, properties)
    }
  },
  debug: (message: string, properties?: LogAttributes): void => {
    if (typeof window !== 'undefined') {
      posthog.logger.debug(message, properties)
    }
  },
  info: (message: string, properties?: LogAttributes): void => {
    if (typeof window !== 'undefined') {
      posthog.logger.info(message, properties)
    }
  },
  warn: (message: string, properties?: LogAttributes): void => {
    if (typeof window !== 'undefined') {
      posthog.logger.warn(message, properties)
    }
  },
  error: (message: string, properties?: LogAttributes): void => {
    if (typeof window !== 'undefined') {
      triggerSessionReplayOnError()
      posthog.logger.error(message, properties)
    }
  },
  fatal: (message: string, properties?: LogAttributes): void => {
    if (typeof window !== 'undefined') {
      triggerSessionReplayOnError()
      posthog.logger.fatal(message, properties)
    }
  },
}

export interface TransferPerformanceData {
  durationMs: number
  bytes: number
  backend?: string
  chunkCount?: number
  averageSpeedBps?: number
  success: boolean
  error?: string
}

/**
 * Capture file transfer (upload or download) performance metrics.
 */
export function trackTransferPerformance(
  type: 'upload' | 'download',
  data: TransferPerformanceData,
  extraProperties?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') {
    return
  }

  const durationSec = Math.max(0.001, data.durationMs / 1000)
  const bytesPerSec =
    data.averageSpeedBps ?? Math.round(data.bytes / durationSec)
  const mbPerSec = Number((bytesPerSec / (1024 * 1024)).toFixed(2))

  posthog.capture('file_transfer_performance', {
    transfer_type: type,
    duration_ms: data.durationMs,
    bytes: data.bytes,
    bytes_per_second: bytesPerSec,
    mb_per_second: mbPerSec,
    backend: data.backend,
    chunk_count: data.chunkCount,
    success: data.success,
    error: data.error,
    ...extraProperties,
  })
}

export { posthog }
